<template>
  <ValidationObserver ref="observer">
    <div class="pa-4">
      <v-title>
        <span class="title">Bundle Management</span>
      </v-title>

      <v-container grid-list-md>
        <v-layout wrap class="pt-3">
          <v-flex lg2>
            <v-combobox
              v-model="bundle"
              :items="bundleList"
              label="Please select bundle plan"
            ></v-combobox>
          </v-flex>
          <v-flex></v-flex>
          <v-flex>
            <div class="justify-center layout px-0">
              <v-btn @click="showInput()" class="primary mt-2">Add New</v-btn>
              <v-btn @click="openCloneDialog()" class="primary mt-2">
                Clone Bundle
              </v-btn>
            </div>
          </v-flex>
          <v-flex lg2>
            <v-text-field
              @input="checkValue($event)"
              v-model="newBundle"
              v-if="inputVisible"
              label="Enter bundle name"
            ></v-text-field>
          </v-flex>
          <v-flex lg1>
            <v-btn
              v-if="saveVisible"
              @click="saveBundle()"
              class="primary mt-2"
            >
              Save
            </v-btn>
          </v-flex>
          <v-flex lg1>
            <v-btn v-if="inputVisible" @click="cancel()" class="mt-2">
              Cancel
            </v-btn>
          </v-flex>
          <v-spacer></v-spacer>
          <v-flex lg3>
            <v-text-field
              v-model="search"
              append-icon="search"
              label="Search"
              single-line
              hide-details
            ></v-text-field>
          </v-flex>
        </v-layout>
      </v-container>

      <v-dialog v-model="editBundle" max-width="800px" persistent>
        <v-card class="pb-3">
          <v-card-title class="primary">
            <span class="headline white--text">Edit Bundle</span>
          </v-card-title>

          <v-card-text>
            <v-container grid-list-md class="pa-1">
              <v-layout wrap>
                <v-flex xs12 sm6 md4>
                  <ValidationProvider
                    name="Bundle ID"
                    rules="required|numeric"
                    immediate
                    v-slot="{ errors }"
                  >
                    <v-text-field
                      v-model="editedItem.bundleId"
                      label="Bundle ID"
                      :error-messages="errors"
                      @change="onChange()"
                    ></v-text-field>
                  </ValidationProvider>
                </v-flex>
                <v-flex xs12 sm6 md4>
                  <v-text-field
                    v-model="editedItem.bundleName"
                    label="Bundle Name"
                  ></v-text-field>
                </v-flex>
                <v-flex xs12 sm6 md4>
                  <ValidationProvider
                    name="Rate Plan ID"
                    rules="required|numeric"
                    immediate
                    v-slot="{ errors }"
                  >
                    <v-text-field
                      :disabled="editedItem.usageType === 'hardware'"
                      v-model="editedItem.ratePlanId"
                      label="Rate Plan"
                      :error-messages="errors"
                      @change="onChange()"
                    ></v-text-field>
                  </ValidationProvider>
                </v-flex>
                <v-flex xs12 sm6 md4>
                  <v-combobox
                    v-model="editedItem.usageType"
                    :items="type"
                    label="Usage Type"
                    @change="checkUsageType()"
                  ></v-combobox>
                </v-flex>
                <v-flex xs12 sm6 md4>
                  <v-text-field
                    v-model="editedItem.detail"
                    label="Detail"
                  ></v-text-field>
                </v-flex>
                <v-flex xs12 sm6 md4>
                  <v-combobox
                    v-model="editedItem.contractMonths"
                    :items="months"
                    label="Contract Months"
                  ></v-combobox>
                </v-flex>
              </v-layout>
              <v-layout wrap>
                <v-flex xs12 sm6 md3>
                  <ValidationProvider
                    name="Service Level ID"
                    immediate
                    v-slot="{ errors }"
                  >
                    <v-text-field
                      disabled
                      v-model="editedItem.serviceLevelID"
                      label="Service Level ID"
                      :error-messages="errors"
                      @change="onChange()"
                    ></v-text-field>
                  </ValidationProvider>
                </v-flex>
                <v-flex xs12 sm6 md3>
                  <ValidationProvider
                    name="Handset ID"
                    rules="required|numeric"
                    immediate
                    v-slot="{ errors }"
                  >
                    <v-text-field
                      :disabled="editedItem.usageType !== 'hardware'"
                      v-model="editedItem.handsetId"
                      label="Handset ID"
                      :error-messages="errors"
                      @change="onChange()"
                    ></v-text-field>
                  </ValidationProvider>
                </v-flex>
                <v-flex xs12 sm6 md3>
                  <ValidationProvider
                    name="RRPInc"
                    rules="required|max_value:100000000.00"
                    immediate
                    v-slot="{ errors }"
                  >
                    <v-text-field
                      :disabled="!(
                        editedItem.usageType === 'hardware' ||
                        (editedItem.usageType === 'mobile' && editedItem.detail === '4G Mobile Backup SIM')
                      )"
                      v-model="editedItem.rrpInc"
                      label="rrpInc"
                      :error-messages="errors"
                      @change="onChange()"
                    ></v-text-field>
                  </ValidationProvider>
                </v-flex>

                <v-flex xs12 sm6 md3>
                  <v-checkbox
                    v-model="editedItem.excludeFromTCV"
                    label="Exclude from TCV"
                  ></v-checkbox>
                </v-flex>
              </v-layout>
            </v-container>
          </v-card-text>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              class="primary"
              :disabled="disable"
              @click="updateDetails(editedItem)"
            >
              Save
            </v-btn>
            <v-btn class="mr-3" @click="closeDialog()">Cancel</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <CloneBundleDialog
        @selectBundle="setBundle"
        :data="bundles"
        ref="cloneBundleDialogRef"
      />

      <v-data-table
        :items="filteredBundles"
        :search="search"
        :loading="loading"
        :headers="headers"
        item-key="id"
        :pagination.sync="pagination"
      >
        <template v-slot:items="props">
          <td>{{ props.item.bundleId }}</td>
          <td>{{ props.item.bundleName }}</td>
          <td>{{ props.item.ratePlanId }}</td>
          <td>{{ props.item.usageType }}</td>
          <td>{{ props.item.detail }}</td>
          <td>{{ props.item.contractMonths }}</td>
          <td>{{ props.item.handsetId }}</td>
          <td>{{ props.item.rrpInc }}</td>
          <td class="justify-center layout px-0">
            <v-btn icon class="mx-0" @click="editDetails(props.item)">
              <v-icon color="teal">edit</v-icon>
            </v-btn>
            <v-btn icon class="mx-0" @click="onDeleteBunle(props.item)">
              <v-icon color="pink">delete</v-icon>
            </v-btn>
          </td>
        </template>
      </v-data-table>
      <v-snackbar v-model="snackbar" :timeout="1000">{{ message }}</v-snackbar>
    </div>
  </ValidationObserver>
</template>

<script>
import { ValidationProvider } from 'vee-validate';
import { createNamespacedHelpers } from 'vuex';
const { mapActions: mapOrderActions } = createNamespacedHelpers(`orders`);
const { mapState: mapOrderState } = createNamespacedHelpers(`orders`);
import CloneBundleDialog from './clone-bundle-dialog';
const { mapGetters: mapFeatureFlagGetters } = createNamespacedHelpers(
  `orders/featureFlag`
);
export default {
  name: 'BundleManagement',
  components: {
    ValidationProvider,
    CloneBundleDialog,
  },
  data() {
    return {
      bundle: 'All',
      newBundle: '',
      search: '',
      inputVisible: false,
      saveVisible: false,
      loading: true,
      editBundle: false,
      editedItem: {},
      type: [
        'nbn',
        'nbnEnterprise',
        'mobile',
        'hardware',
        'hwbb',
        'fiveG',
        'teams',
      ],
      months: [0, 12, 24, 36],
      disable: false,
      pagination: {
        page: 1,
        rowsPerPage: 20,
        totalItems: 0,
      },
      headers: [
        {
          text: 'Bundle ID',
          align: 'left',
          sortable: true,
          value: 'bundleId',
        },
        {
          text: 'Bundle Name',
          align: 'left',
          value: 'bundleName',
        },
        {
          text: 'Rate Plan',
          align: 'left',
          sortable: true,
          value: 'ratePlanId',
        },
        {
          text: 'Usage Type',
          align: 'left',
          sortable: true,
          value: 'usageType',
        },
        {
          text: 'Detail',
          align: 'left',
          sortable: false,
          value: 'detail',
        },
        {
          text: 'Contract Months',
          align: 'left',
          sortable: true,
          value: 'contractMonths',
        },
        {
          text: 'Handset ID',
          align: 'left',
          sortable: true,
          value: 'handsetId',
        },
        {
          text: 'RRPInc',
          align: 'left',
          sortable: true,
          value: 'rrpInc',
        },
        {
          text: 'Actions',
          align: 'left',
          sortable: false,
          value: 'actions',
        },
      ],
      snackbar: false,
      message: '',
    };
  },
  mounted() {
    let vm = this;
    vm.getBundleDetails();
  },
  methods: {
    ...mapOrderActions([
      'getBundles',
      'saveNewBundle',
      'updateBundle',
      'updateServiceLevelOnBundle',
      'deleteBundle',
    ]),
    getBundleDetails() {
      let vm = this;
      vm.getBundles();
      vm.loading = false;
    },
    showInput() {
      let vm = this;
      vm.inputVisible = true;
    },
    saveBundle() {
      let vm = this;
      vm.saveNewBundle(vm.newBundle).then(data => {
        vm.getBundles();
        vm.bundle = vm.newBundle;
        vm.newBundle = '';
      });

      vm.inputVisible = false;
      vm.saveVisible = false;
    },
    checkValue(event) {
      let vm = this;
      if (event !== '') vm.saveVisible = true;
      else vm.saveVisible = false;
    },
    checkUsageType() {
      let vm = this;
      if (vm.editedItem.usageType === 'hardware') vm.editedItem.ratePlanId = 0;
      else vm.editedItem.handsetId = 0;
      vm.editedItem.rrpInc = 0;
    },
    cancel() {
      let vm = this;
      vm.newBundle = '';
      vm.inputVisible = false;
      vm.saveVisible = false;
    },
    editDetails(item) {
      let vm = this;
      vm.editedItem = Object.assign({}, item);
      if (vm.editedItem.detail == null) {
        vm.editedItem.serviceLevelID = 0;
      } else {
        vm.editedItem.serviceLevelID = this.serviceLevelVal;
      }
      vm.editBundle = true;
    },
    closeDialog() {
      let vm = this;
      if (vm.editedItem.serviceLevelID !== null) {
        vm.updateServiceLevelOnBundle({
          id: vm.editedItem.id,
          serviceLevelID: vm.editedItem.serviceLevelID,
        });
      }
      vm.editBundle = false;
    },
    updateDetails(item) {
      let vm = this;
      vm.updateBundle(item).then(data => {
        vm.getBundleDetails();
        vm.editBundle = false;
      });
    },
    onChange() {
      let vm = this;
      vm.$refs.observer.validate().then(result => {
        if (!result) vm.disable = true;
        else vm.disable = false;
      });
    },
    onDeleteBunle(item) {
      let vm = this;
      const confirmDelete = confirm(
        'Are you sure you want to delete this item?'
      );
      if (confirmDelete) {
        vm.deleteBundle(item.id).then(data => {
          vm.snackbar = true;
          vm.message = data;
          vm.getBundles();
        });
      }
    },
    openCloneDialog() {
      let vm = this;
      vm.$refs.cloneBundleDialogRef.openDialog();
    },
    setBundle(value) {
      let vm = this;
      vm.bundle = value;
    },
  },
  computed: {
    ...mapOrderState(['bundles']),
    bundleList() {
      let vm = this;
      var bundlesList = ['All'];

      if (vm.bundles !== null) {
        vm.bundles.forEach(x => {
          bundlesList.push(x.bundleName);
        });
      }

      return bundlesList;
    },
    filteredBundles() {
      let vm = this;

      var filteredList = [];

      if (vm.bundles !== null) {
        if (vm.bundle === 'All') {
          vm.bundles.forEach(x => {
            filteredList.push(x);
          });
        } else {
          vm.bundles.forEach(x => {
            if (x.bundleName === vm.bundle) filteredList.push(x);
          });
        }
      }
      return filteredList;
    },
    serviceLevelVal() {
      let vm = this;
      let detail = vm.editedItem.detail;
      if (vm.editedItem.bundleId > 0) {
        let firstWord = detail
          .split(' ')
          .slice(0, 1)
          .join(' ');
        return vm.editedItem.bundleId + '-' + firstWord;
      }
    },
  },
};
</script>

<style lang="scss" scoped></style>
